// IntelligenceResults.jsx — comps-engine output layer (Track B).
//
// NEW component. Results.js stays FROZEN as the fallback (CLAUDE.md) — read
// from for its price-card visual pattern (Step 1 of this rewrite), never
// edited. This page is reached only when REACT_APP_INTEL_RESULTS === 'true'
// flips the /results route in App.js. It reads the SAME sessionStorage
// handoff ValuationForm writes (pq_result / pq_formData) and renders the real
// POST /valuate payload:
//   estimated_range{low,mid,high}, estimated_psf{low,mid,high},
//   confidence{score,reason,cap,cap_reason}, ring_diagnostics[5],
//   comps_used[]{psf,ring,weight,bhk,sqft,project,facing,message_date,
//     recency_weight,raw_snippet,source_msg_id}, all_psf_values[],
//   locality_match{id,matched_name,score,threshold},
//   psf_context{locality_avg,locality_median,locality_n,your_psf,
//     vs_locality_pct,this_unit,igr_floor{value,source,reason}},
//   adjustment_ledger[]{step,applied,from,to,reason,label}, guard_applied,
//   igr_note, message.
//
// v4 (dashboard-density hero + plain-language range bar — supersedes the v3
// gauge/sparkline build): Section 1's card is a forced-light spotlight card
// (white in BOTH themes — LC #007A8A passes contrast on white, C #00E5FF does
// not, so the dark-mode accent never appears inside it) holding just the
// animated headline number, the min/max range, and a 4-tile stat grid — no
// gauge, no sparkline, no sentence wrapping the number. Section 3 is a
// percentile range bar (gradient track + denser 25th-75th band + teardrop
// pin), not a dot-strip, and never uses statistical vocabulary in visible
// text. Sections 2 and 3 are genuinely interactive (tap-to-highlight/tap-to-
// read, scroll-triggered entrance). API contract is untouched beyond two
// additive fields from an earlier session: all_psf_values (uncapped psf
// array) and GET /rental-comps/{id} (comparables has no public RLS policy —
// see docs/tech_debt.md Priority 7). Honesty rules honoured throughout: never
// invents a price; the comps shown ARE the proof; confidence is rendered
// against the engine's own score; every ledger line (including the Full
// Story math narrative and the always-false asking_haircut) is read live
// from the engine's own ledger, never hardcoded.

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, animate } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, LabelList } from "recharts";
import axios from "axios";
import { useTheme } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}`;
const C = "#00E5FF";   // dark accent
const LC = "#007A8A";  // light accent
const BG = "#080d16";
const LIGHT_BG = "#EEF6F8";

// ── semantic color system ───────────────────────────────────────────────────
// Same meaning, same exact value, everywhere in this file. Light/dark pairs
// exist only for contrast (mirrors the C/LC accent pattern above) — within a
// theme, each constant never varies by location. Reserve LC/C themselves for
// "this is about YOUR property" (hero price, You marker/pill) — never reuse
// them for a good/bad judgement, and never use these three for anything that
// isn't a cheaper/pricier/neutral comparison.
const POSITIVE_L = "#1C8F5A", POSITIVE_D = "#34D399"; // cheaper than your estimate
const CAUTION_L = "#B45309", CAUTION_D = "#FBBF24";   // pricier than your estimate
const NEUTRAL_L = "#6A6560", NEUTRAL_D = "#94a3b8";   // medians / reference points
const WA_NUMBER = "918884108992"; // same number as Home.js footer

// ── handoff (sessionStorage first — survives refresh — then router state) ─────
function readHandoff(location) {
  let result = null, formData = null;
  try {
    const r = sessionStorage.getItem("pq_result");
    if (r) result = JSON.parse(r);
  } catch { /* corrupted — fall through, formData may still resolve */ }
  try {
    const f = sessionStorage.getItem("pq_formData");
    if (f) formData = JSON.parse(f);
  } catch { /* ignore */ }
  if (!result && location.state?.result) result = location.state.result;
  if (!formData && location.state?.formData) formData = location.state.formData;
  return { result, formData };
}

function buildValuatePayload(formData) {
  return {
    locality: formData.locality,
    project: formData.project || null,
    built_up_area: Number(formData.built_up_area),
    bhk: parseInt(formData.bhk),
    building_age: parseInt(formData.building_age),
    asking_price: formData.asking_price ? parseFloat(formData.asking_price) : null,
    floor_number: parseInt(formData.floor_number) || 0,
  };
}

// ── tiny shared helpers ────────────────────────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth <= 640);
  useEffect(() => {
    const fn = () => setM(window.innerWidth <= 640);
    window.addEventListener("resize", fn, { passive: true });
    return () => window.removeEventListener("resize", fn);
  }, []);
  return m;
}

// Identical cubic ease-out to Results.js's useCounter — same animation DNA.
function useCounter(target, dur = 2200) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, t0 = null;
    const step = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setV(e * target);
      if (p < 1) raf = requestAnimationFrame(step); else setV(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
}

const fmtCr = (v) => {
  if (v == null) return "—";
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};
const fmtPsf = (v) => (v == null ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`);

// ── left-to-right gradient color across a bar set — stays within the teal/
// cyan accent family (cooler+lighter for cheap, deeper+warmer for pricey) ──
const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const lerpColor = (hexA, hexB, t) => {
  const [r1, g1, b1] = hexToRgb(hexA), [r2, g2, b2] = hexToRgb(hexB);
  const r = Math.round(r1 + (r2 - r1) * t), g = Math.round(g1 + (g2 - g1) * t), b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b})`;
};
const GRADIENT_ENDS = {
  light: ["#A8E0E6", "#0F5C68"],
  dark: ["#2E8FA0", "#083B44"],
};
function barGradientColor(idx, count, il) {
  const [from, to] = il ? GRADIENT_ENDS.light : GRADIENT_ENDS.dark;
  const t = count > 1 ? idx / (count - 1) : 0;
  return lerpColor(from, to, t);
}
const fmtMonth = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};
// "Listed 2 months ago" style relative recency for the real-listings rows.
const relativeRecency = (iso) => {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(d)) return null;
  const now = new Date();
  const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (months <= 0) return "Listed this month";
  if (months === 1) return "Listed 1 month ago";
  if (months < 12) return `Listed ${months} months ago`;
  const years = Math.floor(months / 12);
  return `Listed ${years} year${years === 1 ? "" : "s"} ago`;
};
const titleCase = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const withArticle = (s) => {
  const l = lowerFirst(s);
  return /^[aeiou]/i.test(l) ? `an ${l}` : `a ${l}`;
};
// Friendlier labels for adjustment-ledger factor names than a raw titleCase would give.
const ADJ_LABEL = { floor: "Floor", age: "Building Age", facing: "Facing", khata: "Khata" };
const adjLabel = (factor) => ADJ_LABEL[factor] || titleCase(factor);

// ── ring-diagnostics helpers ───────────────────────────────────────────────────
const RING_PLAIN = {
  1: "Exact building match",
  2: "Same building, any size",
  3: "Similar-sized homes nearby",
  4: "Same size homes in the area",
  5: "Any home in the area",
};
function widestActivatedRing(rings) {
  const act = (rings || []).filter((r) => r.activated);
  if (!act.length) return null;
  return act.reduce((a, b) => (b.ring > a.ring ? b : a));
}
// "widest fired ring's comp count" — the trust stat, not comps_used.length
// (capped at 20 for display).
function trustCompCount(result) {
  const widest = widestActivatedRing(result?.ring_diagnostics);
  return widest?.comps_found ?? (result?.comps_used?.length || 0);
}

// ── histogram builder (₹/sqft buckets) ─────────────────────────────────────────
function buildHistogram(values, targetBuckets = 7) {
  const vals = (values || []).filter((v) => Number.isFinite(v));
  if (vals.length < 5) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  if (max <= min) return null;
  const rawStep = (max - min) / targetBuckets;
  const step = Math.max(500, Math.ceil(rawStep / 500) * 500);
  const start = Math.floor(min / step) * step;
  const bucketCount = Math.max(1, Math.ceil((max - start) / step));
  const buckets = Array.from({ length: bucketCount }, (_, i) => {
    const low = start + i * step, high = low + step;
    return { low, high, count: 0, label: `₹${Math.round(low / 1000)}-${Math.round(high / 1000)}K`, i };
  });
  vals.forEach((v) => {
    let idx = Math.floor((v - start) / step);
    idx = Math.max(0, Math.min(buckets.length - 1, idx));
    buckets[idx].count += 1;
  });
  return { buckets, start, step, n: vals.length };
}
function bucketIndexFor(hist, value) {
  if (!hist || value == null) return -1;
  const idx = Math.floor((value - hist.start) / hist.step);
  return Math.max(0, Math.min(hist.buckets.length - 1, idx));
}

// ── whatsapp deeplink (product decision — CTA opens WA, not /inspect) ─────────
function waInspectionHref(result, formData) {
  const mid = result?.estimated_range?.mid;
  const locality = result?.locality_match?.matched_name || formData?.locality || "";
  const lines = [
    "Hi! I'd like to book a property inspection.",
    "",
    `Locality: ${locality}`,
    formData?.bhk ? `BHK: ${formData.bhk}` : null,
    formData?.built_up_area ? `Built-up area: ${Number(formData.built_up_area).toLocaleString()} sqft` : null,
    mid != null ? `Estimate: ${fmtCr(mid)}` : null,
    "",
    "Please share next steps.",
  ].filter(Boolean);
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
}

// ── rental comps (new endpoint — RLS has no public policy on comparables) ─────
function useRentalComps(localityId, enabled) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!enabled || !localityId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/rental-comps/${localityId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ available: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [localityId, enabled]);
  return { data, loading };
}

// ── real named listings (new endpoint — Section 2's "Evidence" sub-part) ──────
function useRecentListings(localityId, bhk, enabled) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled || !localityId) return;
    let cancelled = false;
    axios.get(`${API}/recent-listings/${localityId}`, { params: bhk ? { bhk } : {} })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ available: false, listings: [] }); });
    return () => { cancelled = true; };
  }, [localityId, bhk, enabled]);
  return data;
}

// ── price trend (new endpoint — needs a real DB volume check on both sides
// of the window, which comps_used/all_psf_values can't answer client-side) ──
function usePriceTrend(localityId, enabled) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled || !localityId) return;
    let cancelled = false;
    axios.get(`${API}/price-trend/${localityId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ available: false }); });
    return () => { cancelled = true; };
  }, [localityId, enabled]);
  return data;
}

// ── price drivers — simple group-by comparisons over comps_used (the only
// per-comp-attribute array the /valuate response carries; all_psf_values is
// psf-only, no sqft/facing). Every reported delta is backed by ≥5 real comps
// per group on both sides — thin groups are silently excluded, never forced
// into a percentage. Floor isn't computed at all: comps_used has no floor
// field (see file-header field list), so that's not "sparse", it's absent.
function computePriceDrivers(comps) {
  const MIN_PER_GROUP = 5;
  const valid = (comps || []).filter((c) => Number.isFinite(c.psf));
  const avg = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
  const upperFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const drivers = [];

  const SQFT_BUCKETS = [
    { label: "<1,500 sqft", short: "compact", test: (s) => s < 1500 },
    { label: "1,500–2,500 sqft", short: "mid-size", test: (s) => s >= 1500 && s < 2500 },
    { label: "2,500+ sqft", short: "larger", test: (s) => s >= 2500 },
  ];
  const sqftGroups = SQFT_BUCKETS
    .map((b) => ({ ...b, vals: valid.filter((c) => Number.isFinite(c.sqft) && b.test(c.sqft)).map((c) => c.psf) }))
    .filter((g) => g.vals.length >= MIN_PER_GROUP);
  if (sqftGroups.length >= 2) {
    const small = sqftGroups[0], large = sqftGroups[sqftGroups.length - 1];
    const smallAvg = avg(small.vals), largeAvg = avg(large.vals);
    const pct = Math.abs(((largeAvg - smallAvg) / smallAvg) * 100);
    if (pct >= 1) {
      const winner = largeAvg > smallAvg ? large : small;
      const loser = largeAvg > smallAvg ? small : large;
      drivers.push(`${upperFirst(winner.short)} units (${winner.label}) command ~${pct.toFixed(0)}% more per sqft than ${loser.short} ones`);
    }
  }

  const facingMap = {};
  valid.forEach((c) => {
    const f = (c.facing || "").trim();
    if (f) (facingMap[f] ||= []).push(c.psf);
  });
  const facingGroups = Object.entries(facingMap)
    .map(([facing, vals]) => ({ facing, vals, avg: avg(vals) }))
    .filter((g) => g.vals.length >= MIN_PER_GROUP)
    .sort((a, b) => b.avg - a.avg);
  if (facingGroups.length >= 2) {
    const hi = facingGroups[0], lo = facingGroups[facingGroups.length - 1];
    const pct = ((hi.avg - lo.avg) / lo.avg) * 100;
    if (pct >= 1) {
      drivers.push(`${hi.facing}-facing units command ~${pct.toFixed(0)}% more per sqft than ${lo.facing}-facing ones`);
    }
  }

  return drivers.slice(0, 3);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
function GCard({ children, style = {}, testId, onClick }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      style={{
        background: il ? "#FFFFFF" : "rgba(10,15,26,0.62)",
        backdropFilter: il ? "none" : "blur(24px)",
        WebkitBackdropFilter: il ? "none" : "blur(24px)",
        border: il ? "1.5px solid rgba(0,189,204,0.22)" : "1px solid rgba(255,255,255,0.08)",
        borderRadius: 16,
        padding: isMob ? "18px 16px" : "24px 28px",
        position: "relative",
        overflow: "hidden",
        boxShadow: il ? "0 2px 20px rgba(0,189,204,0.09)" : "none",
        transition: "all 0.2s ease",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  return (
    <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: isMob ? "1.28rem" : "1.55rem", color: il ? "#0F0E0C" : "#f1f5f9", margin: "0 0 14px" }}>{children}</h2>
  );
}

function CTABtn({ children, onClick, href, variant = "cyan", testId, style: extraStyle }) {
  const [hov, setHov] = useState(false);
  const { theme } = useTheme();
  const il = theme !== "dark";
  const styles = {
    cyan: il
      ? { background: hov ? "#00697a" : "#007A8A", color: "#fff", border: "none", boxShadow: hov ? "0 8px 24px rgba(0,122,138,0.28)" : "none", transform: hov ? "translateY(-1px)" : "none" }
      : { background: hov ? "rgba(0,229,255,0.18)" : "rgba(0,229,255,0.10)", color: C, border: "1px solid rgba(0,229,255,0.4)", boxShadow: hov ? "0 0 26px rgba(0,229,255,0.22)" : "none", transform: hov ? "translateY(-1px)" : "none" },
    ghost: il
      ? { background: "#F0EDEA", color: "#0F0E0C", border: "1px solid rgba(0,0,0,0.10)" }
      : { background: hov ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.05)", color: hov ? "#fff" : "#e2e8f0", border: "1px solid rgba(255,255,255,0.1)" },
  };
  const Tag = href ? "a" : "button";
  return (
    <Tag data-testid={testId} href={href} target={href ? "_blank" : undefined} rel={href ? "noopener noreferrer" : undefined}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.86rem", padding: "14px 24px", borderRadius: 50, cursor: "pointer", border: "none", textDecoration: "none", transition: "all 0.2s ease", ...styles[variant], ...extraStyle }}>
      {children}
    </Tag>
  );
}

// Top-level collapsible card — used by Section 7 ("The Full Story").
// muted=true is Section 4's whole point: deliberately the least visually
// loud card on the page (flat grey, no accent tint on the icon chip either),
// reinforcing that this is secondary/optional content for power users.
function Collapsible({ icon, title, badge, defaultOpen = false, children, testId, muted = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const { theme } = useTheme();
  const il = theme !== "dark";
  return (
    <GCard testId={testId} style={muted ? { background: il ? "#F6F5F3" : "rgba(22,22,24,0.6)", border: il ? "1.5px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.06)", boxShadow: "none" } : {}}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", transition: "all 0.2s ease" }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: muted ? (il ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)") : (il ? "rgba(0,189,204,0.08)" : "rgba(0,229,255,0.08)"), border: muted ? (il ? "1px solid rgba(0,0,0,0.1)" : "1px solid rgba(255,255,255,0.08)") : (il ? "1px solid rgba(0,189,204,0.18)" : "1px solid rgba(0,229,255,0.15)"), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.92rem", color: il ? "#0F0E0C" : "#f1f5f9" }}>{title}</div>
          {badge && <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.68rem", color: il ? "#6A6560" : "#94a3b8", marginTop: 2 }}>{badge}</div>}
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={il ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.35)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "none", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="c" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </GCard>
  );
}

// Nested accordion used only inside Section 7 (avoids card-in-card borders).
function SubAccordion({ title, badge, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const { theme } = useTheme();
  const il = theme !== "dark";
  return (
    <div style={{ borderTop: il ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(255,255,255,0.06)", paddingTop: 14, marginTop: 14 }}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", transition: "all 0.2s ease" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.84rem", color: il ? "#0F0E0C" : "#e2e8f0" }}>{title}</div>
          {badge && <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.66rem", color: il ? "#9A9490" : "#64748b", marginTop: 2 }}>{badge}</div>}
        </div>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={il ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.35)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "none", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="c" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
            <div style={{ paddingTop: 12 }}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 — HERO VERDICT (rebuilt on Results.js's original price-card DNA)
// ─────────────────────────────────────────────────────────────────────────────
// Forced-light stat tile — lives inside the hero card, which is always
// rendered light (see HeroVerdict), so it never needs a theme branch.
function HeroStatTile({ icon, label, value, sub }) {
  const isMob = useIsMobile();
  return (
    <div style={{
      background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 12,
      padding: isMob ? "12px 12px" : "14px 16px", transition: "all 0.2s ease",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(0,122,138,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
        <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.56rem", color: "#6A6560", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>{label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: isMob ? "1.05rem" : "1.15rem", color: "#0F0E0C", letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 600, fontSize: "0.66rem", color: "#9A9490", lineHeight: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

// Score maps to a qualitative read as the primary signal — "{score}/100" reads
// like a failing grade to a consumer even when the number is honest. The math
// and the 55-cap discipline behind it are untouched; this only changes how the
// same number is framed. Avoid grade-sounding words ("Poor", "Weak").
function confidenceLabel(score) {
  if (score >= 50) return "Strong";
  if (score >= 40) return "Fair";
  if (score >= 30) return "Limited";
  return "Early Read";
}

const HERO_ICONS = {
  psf: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={LC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  confidence: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={LC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  yield: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={LC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
  comps: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={LC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
  sample: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={LC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>,
  median: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={LC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M18.5 8l-5 5-3-3-4 4" /></svg>,
};

function HeroVerdict({ result, formData, rental }) {
  const isMob = useIsMobile();
  const range = result.estimated_range;
  const mid = range.mid;
  // Same trick as Results.js: decide Cr-vs-L format off the FINAL target, not
  // the live animated value, so the unit never flickers mid count-up. Shorter
  // duration than Results.js (500-700ms) — this card wants a snap, not a crawl.
  const animMid = useCounter(mid || 0, 650);
  const liveFmt = mid >= 10000000 ? `₹${(animMid / 10000000).toFixed(2)} Cr` : `₹${(animMid / 100000).toFixed(2)} L`;
  const score = result.confidence?.score ?? 0;
  const lo = range.low, hi = range.high;
  const psf = result.estimated_psf?.mid ?? result.psf_context?.this_unit;
  const n = trustCompCount(result);
  const hasRental = !!rental?.available;
  const grossYield = hasRental && mid ? ((rental.median_rent * 12 / mid) * 100).toFixed(1) : null;
  const localityMedian = result.psf_context?.locality_median;

  // Tile 3/4 pick real, non-duplicated stats — never an invented ROI figure.
  const tile3 = hasRental
    ? { icon: HERO_ICONS.yield, label: "Rental Yield", value: grossYield ? `${grossYield}%` : "—" }
    : { icon: HERO_ICONS.comps, label: "Comps Used", value: n.toLocaleString() };
  const tile4 = hasRental
    ? { icon: HERO_ICONS.sample, label: "Sample Size", value: `${n.toLocaleString()} listing${n === 1 ? "" : "s"}` }
    : { icon: HERO_ICONS.median, label: "Locality Median", value: localityMedian != null ? `${fmtPsf(localityMedian)}/sqft` : "—" };

  return (
    <section style={{ padding: isMob ? "36px 16px 8px" : "52px 40px 8px" }}>
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative", width: 7, height: 7, flexShrink: 0 }}>
            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: C }} />
            <div style={{ position: "absolute", inset: -3, borderRadius: "50%", border: "1px solid rgba(0,229,255,0.65)", animation: "pulse-ring 2s ease-out infinite" }} />
          </div>
          <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.7rem", color: "rgba(120,120,120,0.9)", fontWeight: 600 }}>Valuation Complete</span>
        </div>
      </div>

      {/* Card is deliberately forced light in BOTH themes — a bright, dashboard-
          density spotlight card sitting on the page's own (possibly dark) bg.
          LC (#007A8A) passes contrast on white (~5:1); C (#00E5FF) would not
          (~1.5:1), so the dark-mode accent is never used inside this card. */}
      <div data-testid="main-valuation-card" style={{
        background: "#FFFFFF", border: "1px solid rgba(0,189,204,0.22)", borderRadius: 18,
        padding: isMob ? "28px 20px" : "44px 48px", textAlign: "center",
        boxShadow: "0 4px 32px rgba(0,189,204,0.12)", position: "relative", overflow: "hidden",
      }}>
        {/* Optional accent-tinted corner glow for richness — base stays light */}
        <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,189,204,0.14) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.62rem", color: "#6A6560", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700, marginBottom: 12 }}>Estimated Property Value</div>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: isMob ? "clamp(2.3rem,13vw,3rem)" : "clamp(2.8rem,6vw,4rem)", color: LC, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 12 }}>
            {liveFmt}
          </div>

          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 18, marginBottom: 24 }}>
            <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.76rem", color: "#6A6560" }}>Min <strong style={{ color: "#0F0E0C" }}>{fmtCr(lo)}</strong></span>
            <div style={{ width: 1, height: 12, background: "rgba(0,0,0,0.12)" }} />
            <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.76rem", color: "#6A6560" }}>Max <strong style={{ color: "#0F0E0C" }}>{fmtCr(hi)}</strong></span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr 1fr" : "repeat(4,1fr)", gap: 10, textAlign: "left" }}>
            <HeroStatTile icon={HERO_ICONS.psf} label="Price / sqft" value={psf != null ? fmtPsf(psf) : "—"} />
            <HeroStatTile icon={HERO_ICONS.confidence} label="Confidence" value={confidenceLabel(score)} sub={`${Math.round(score)}/100`} />
            <HeroStatTile icon={tile3.icon} label={tile3.label} value={tile3.value} />
            <HeroStatTile icon={tile4.icon} label={tile4.label} value={tile4.value} />
          </div>

          <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.72rem", color: LC, lineHeight: 1.5, margin: "14px 0 0" }}>
            Book an inspection to lift this to 80+
          </p>
        </div>
      </div>
    </section>
  );
}

function HeroEmpty({ result, formData }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const msg = result?.message || "We don't have enough data on this locality yet.";
  return (
    <section style={{ padding: isMob ? "36px 16px 8px" : "52px 40px 8px" }}>
      <div style={{
        background: il ? "linear-gradient(135deg, rgba(0,189,204,0.05) 0%, #FFFFFF 60%)" : "linear-gradient(135deg,rgba(0,229,255,0.03) 0%,rgba(10,15,26,0.82) 55%)",
        border: il ? "2px solid rgba(0,189,204,0.2)" : "1px solid rgba(0,229,255,0.14)",
        borderRadius: 20, padding: isMob ? "36px 22px" : "56px 60px", textAlign: "center",
      }}>
        <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.62rem", color: il ? "#6A6560" : "#e2e8f0", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700, marginBottom: 16 }}>Our Estimate</div>
        <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: isMob ? "1.55rem" : "2rem", color: il ? "#0F0E0C" : "#f1f5f9", margin: "0 0 14px", lineHeight: 1.3 }}>Not enough data yet</h1>
        <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.85rem", color: il ? "#6A6560" : "#94a3b8", lineHeight: 1.65, margin: "0 auto", maxWidth: 440 }}>{msg} Book an inspection for a direct, verified read.</p>
        {formData?.locality && (
          <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.7rem", color: il ? "#9A9490" : "#64748b", margin: "14px 0 0" }}>{formData.bhk} BHK · {formData.locality} · {Number(formData?.built_up_area || 0).toLocaleString()} sqft</p>
        )}
      </div>
    </section>
  );
}

// Understated reminder of what's being valued — sits between the Hero card
// and "The Evidence" so it's never mistaken for its own section (no card, no
// border, just small quiet text) but the context survives a long scroll.
function PropertySummaryRow({ result, formData }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const locality = result?.locality_match?.matched_name || formData?.locality;
  const parts = [
    formData?.bhk ? `${formData.bhk} BHK` : null,
    formData?.built_up_area ? `${Number(formData.built_up_area).toLocaleString()} sqft` : null,
    locality || null,
    formData?.facing || null,
  ].filter(Boolean);
  if (parts.length < 2) return null;
  return (
    <div style={{ textAlign: "center", padding: isMob ? "10px 16px 0" : "12px 40px 0" }}>
      <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.72rem", color: il ? "#9A9490" : "#64748b", margin: 0 }}>{parts.join(" · ")}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 2 — THE EVIDENCE: (a) interactive histogram, (b) real named listings
// ─────────────────────────────────────────────────────────────────────────────
// Custom bar shape — height/y are driven by an external per-bar progress
// value (0→1, animated via Framer Motion's spring in the parent) rather than
// Recharts' own linear bar animation, so growth can be staggered + bouncy.
// Default (unhighlighted) fill is a left-to-right gradient across the bar
// set; the "yours" bucket always overrides to the flat accent + a glow so it
// never gets lost inside the gradient sweep.
function makeHistBarShape(tappedIdx, yourIdx, accent, il, anyTapped, progressRef, bucketCount) {
  return function HistBarShape(props) {
    const { x, y, width, height, payload } = props;
    const idx = payload?.i;
    const isTapped = idx === tappedIdx;
    const isYours = idx === yourIdx;
    const progress = progressRef.current[idx] ?? 0;
    const fill = isTapped || isYours ? accent : barGradientColor(idx, bucketCount, il);
    const opacity = isTapped || isYours ? 1 : anyTapped ? 0.55 : 0.9;
    const scale = isTapped ? 1.08 : 1;
    const w2 = width * scale;
    const x2 = x - (w2 - width) / 2;
    const grownH = height * progress;
    const h2 = isTapped ? grownH + 4 : grownH;
    const y2 = y + height - h2;
    return (
      <rect x={x2} y={y2} width={w2} height={Math.max(0, h2)} rx={6} ry={6} fill={fill} opacity={opacity}
        data-bar-idx={idx}
        style={{
          transition: "x 0.2s ease, width 0.2s ease, opacity 0.2s ease, fill 0.2s ease, filter 0.2s ease",
          filter: isYours ? `drop-shadow(0 0 6px ${accent}aa)` : "none",
          cursor: "pointer",
        }} />
    );
  };
}

// Bold count label rendered directly above each bar by default, counting up
// in sync with the same progress value driving its bar's growth.
function makeCountLabel(yourIdx, accent, il, pillIdx, progressRef) {
  return function CountLabel(props) {
    const { x, y, width, height, value, index } = props;
    if (!value) return null;
    // The bucket already carrying the You/Median pill skips its own floating
    // count — the pill already marks it, and the exact number is one tap
    // away — rather than crowd the pill with a number sitting right below it.
    if (index === pillIdx) return null;
    const progress = progressRef.current[index] ?? 0;
    const displayed = Math.round(value * progress);
    if (!displayed) return null;
    // Mirrors the bar shape's own grow-from-bottom math so the number tracks
    // the bar's top edge exactly, frame for frame.
    const animY = y + height * (1 - progress);
    const isYours = index === yourIdx;
    return (
      <text x={x + width / 2} y={animY - 7} textAnchor="middle" fontFamily="'Sora',sans-serif" fontWeight={800}
        fontSize={11} fill={isYours ? accent : il ? "#0F0E0C" : "#f1f5f9"}>
        {displayed}
      </text>
    );
  };
}

// Filled pill sitting directly on the reference line — not floating text
// nearby — so "You" / "Median" are unmissable at a glance. Drops in with a
// spring bounce after the bars settle, pulses once to draw the eye, then
// idles with a barely-there breathing glow.
function RefLinePill({ viewBox, text, bg, fg, yOffset, phase }) {
  if (!viewBox) return null;
  const w = Math.max(38, text.length * 7 + 18);
  const y = (viewBox.y ?? 0) + yOffset;
  const cx = viewBox.x, cy = y + 9;
  const visible = phase >= 1;
  const idle = phase >= 3;
  return (
    <motion.g
      pointerEvents="none"
      style={{ transformOrigin: `${cx}px ${cy}px` }}
      initial={{ opacity: 0, y: -14, scale: 0.8 }}
      animate={
        !visible ? { opacity: 0, y: -14, scale: 0.8 }
          : phase === 2 ? { opacity: 1, y: 0, scale: [1, 1.12, 1] }
          : idle ? { opacity: 1, y: 0, scale: [1, 1.035, 1] }
          : { opacity: 1, y: 0, scale: 1 }
      }
      transition={
        !visible ? { duration: 0 }
          : phase === 2 ? { duration: 0.45, ease: "easeInOut" }
          : idle ? { duration: 2.4, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" }
          : { type: "spring", bounce: 0.6, duration: 0.6 }
      }
    >
      <rect x={viewBox.x - w / 2} y={y} width={w} height={18} rx={9} fill={bg} />
      <text x={viewBox.x} y={y + 13} textAnchor="middle" fontFamily="'Sora',sans-serif" fontWeight={800} fontSize={10.5} fill={fg}>
        {text}
      </text>
    </motion.g>
  );
}

// Real named listing row — the second half of "The Evidence": actual
// projects, not just statistics. psf color is a cheaper/pricier-than-you
// comparison, so it's semantic (Part B wires the exact constants).
function ListingRow({ listing, yourPsf, il, isLast, cheaperColor, pricierColor, neutralColor }) {
  const cheaper = yourPsf != null ? listing.psf < yourPsf : null;
  const psfColor = cheaper == null ? (il ? "#0F0E0C" : "#f1f5f9") : cheaper ? cheaperColor : pricierColor;
  const recency = relativeRecency(listing.message_date);
  return (
    <div style={{ padding: "12px 0", borderBottom: isLast ? "none" : (il ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(255,255,255,0.06)") }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.86rem", color: il ? "#0F0E0C" : "#f1f5f9" }}>{listing.project_name}</div>
        <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: "0.86rem", color: psfColor, flexShrink: 0, whiteSpace: "nowrap" }}>{fmtPsf(listing.psf)}/sqft</div>
      </div>
      <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.72rem", color: neutralColor, marginTop: 3 }}>
        {listing.bhk} BHK · {Math.round(listing.sqft).toLocaleString()} sqft{listing.facing ? ` · ${listing.facing} facing` : ""}{recency ? ` · ${recency}` : ""}
      </div>
    </div>
  );
}

function DistributionSection({ result, formData, listingsData, trendData }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const accent = il ? LC : C;
  const locality = result.locality_match?.matched_name || formData?.locality || "the locality";
  const ctx = result.psf_context;

  const fullSample = result.all_psf_values?.length >= 5;
  const sampleValues = fullSample ? result.all_psf_values : (result.comps_used || []).map((c) => c.psf).filter(Number.isFinite);
  const hist = useMemo(() => buildHistogram(sampleValues, isMob ? 6 : 8), [sampleValues, isMob]);
  const driverLines = useMemo(() => computePriceDrivers(result.comps_used), [result.comps_used]);

  const [inView, setInView] = useState(false);
  const [tappedIdx, setTappedIdx] = useState(-1);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Per-bar entrance progress (0→1) lives in a ref — mutated every animation
  // frame by the spring drivers below — paired with a cheap tick counter to
  // force the re-renders that make Recharts re-invoke the shape/label
  // functions reading it. Avoids recreating an array every frame.
  const progressRef = useRef([]);
  const [, forceTick] = useState(0);
  // 0 = bars not yet grown, 1 = You/Median pill dropped in, 2 = pill pulsing
  // once, 3 = idle (pill settles into its barely-there breathing glow).
  const [pillPhase, setPillPhase] = useState(0);

  // All hooks must run before any early return (rules-of-hooks) — computed
  // with safe fallbacks so they're harmless on the "not enough data" path.
  const yourIdx = hist ? bucketIndexFor(hist, ctx?.your_psf) : -1;
  const medIdx = hist ? bucketIndexFor(hist, ctx?.locality_median) : -1;
  const pillIdx = medIdx >= 0 && medIdx === yourIdx ? yourIdx : -1;
  const bucketCount = hist?.buckets?.length ?? 0;

  // Staggered spring entrance: each bar's height + its count-up number ride
  // the same progress value, so they can never drift out of sync. Once bars
  // have settled, the You/Median pill drops in, pulses once, then idles.
  useEffect(() => {
    if (!inView || bucketCount === 0) return;
    let cancelled = false;
    progressRef.current = new Array(bucketCount).fill(0);
    const stagger = 0.07; // 70ms between each bar's start — within the 60-80ms ask
    const controls = Array.from({ length: bucketCount }, (_, i) =>
      animate(0, 1, {
        type: "spring", stiffness: 210, damping: 15, delay: i * stagger,
        onUpdate: (v) => {
          if (cancelled) return;
          progressRef.current[i] = v;
          forceTick((t) => t + 1);
        },
      })
    );
    const springSettleMs = 650; // approx settle time for the configured spring
    const pillDelay = bucketCount * stagger * 1000 + springSettleMs + 260; // "200-300ms after last bar lands"
    const t1 = setTimeout(() => !cancelled && setPillPhase(1), pillDelay);
    const t2 = setTimeout(() => !cancelled && setPillPhase(2), pillDelay + 600);
    const t3 = setTimeout(() => !cancelled && setPillPhase(3), pillDelay + 600 + 500);
    return () => {
      cancelled = true;
      controls.forEach((c) => c.stop());
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, bucketCount]);

  const barShape = useMemo(() => makeHistBarShape(tappedIdx, yourIdx, accent, il, tappedIdx >= 0, progressRef, bucketCount), [tappedIdx, yourIdx, accent, il, bucketCount]);
  const countLabel = useMemo(() => makeCountLabel(yourIdx, accent, il, pillIdx, progressRef), [yourIdx, accent, il, pillIdx]);

  // Drag-to-scrub: pointermove (mouse hover OR touch drag — touch only ever
  // fires move while in contact, so this is "hover" on desktop and "drag" on
  // mobile for free) finds the bar under the pointer via each rect's own
  // bounding box, so it stays correct regardless of chart margins/scaling. A
  // plain tap is just a drag with no movement, so it rides the same path.
  const chartWrapRef = useRef(null);
  const [bubble, setBubble] = useState(null); // { left, bucket } — follows the pointer
  const clearScrub = () => {
    setTappedIdx(-1);
    setBubble(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  };
  const scrubToClientX = (clientX) => {
    const wrapRect = chartWrapRef.current?.getBoundingClientRect();
    const rects = chartWrapRef.current?.querySelectorAll("[data-bar-idx]");
    if (!rects || !wrapRect) return;
    let found = -1, foundRect = null;
    rects.forEach((r) => {
      const rect = r.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) { found = Number(r.getAttribute("data-bar-idx")); foundRect = rect; }
    });
    if (found >= 0) {
      setTappedIdx(found);
      setBubble({ left: foundRect.left + foundRect.width / 2 - wrapRect.left, bucket: data?.[found] ?? hist.buckets[found] });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(clearScrub, 4000);
    }
  };
  const onScrubStart = (e) => { e.stopPropagation(); scrubToClientX(e.clientX); };
  const onScrubMove = (e) => scrubToClientX(e.clientX);
  const onScrubLeave = (e) => { if (e.pointerType === "mouse") clearScrub(); };
  const onBarTap = (idx) => {
    setTappedIdx((prev) => (prev === idx ? -1 : idx));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTappedIdx(-1), 4000);
  };

  if (!hist || !ctx) {
    return (
      <section style={{ padding: isMob ? "8px 16px 20px" : "8px 40px 20px" }}>
        <SectionHeading>The Evidence</SectionHeading>
        <GCard><p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.85rem", color: il ? "#6A6560" : "#94a3b8", margin: 0, textAlign: "center" }}>Not enough listings yet to chart a distribution.</p></GCard>
      </section>
    );
  }

  const data = hist.buckets;
  const pct = ctx.vs_locality_pct;
  const above = pct != null && pct >= 0;
  const tapped = tappedIdx >= 0 ? hist.buckets[tappedIdx] : null;

  return (
    <section style={{ padding: isMob ? "8px 16px 20px" : "8px 40px 20px" }}>
      <SectionHeading>The Evidence</SectionHeading>
      {!fullSample && (
        <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.68rem", color: il ? "#9A9490" : "#64748b", margin: "-8px 0 14px" }}>Based on a sample of {hist.n} comps</p>
      )}
      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} onViewportEnter={() => setInView(true)} transition={{ duration: 0.5 }}>
        <GCard onClick={clearScrub} style={{ background: il ? "#F3FBFC" : "rgba(11,32,37,0.68)" }}>
          {inView ? (
            <div ref={chartWrapRef} style={{ position: "relative" }}>
              {/* Transparent scrub overlay — sits above the SVG, drives
                  continuous highlight via pointer position rather than a
                  discrete click. A plain tap is just a drag with no movement,
                  so it falls out of the same code path automatically. */}
              <div
                onPointerDown={onScrubStart}
                onPointerMove={onScrubMove}
                onPointerLeave={onScrubLeave}
                style={{ position: "absolute", inset: 0, zIndex: 2, cursor: "grab", touchAction: "pan-y" }}
              />
              {bubble && (
                <div style={{
                  position: "absolute", left: bubble.left, top: isMob ? 92 : 98, transform: "translateX(-50%)", zIndex: 3, pointerEvents: "none",
                  background: il ? "#FFFFFF" : "rgba(8,13,22,0.96)", border: il ? "1px solid rgba(0,189,204,0.25)" : "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8, padding: "5px 10px", fontFamily: "'Manrope',sans-serif", fontSize: "0.66rem", fontWeight: 700,
                  color: il ? "#0F0E0C" : "#f1f5f9", whiteSpace: "nowrap", boxShadow: "0 6px 16px rgba(0,0,0,0.16)",
                }}>
                  ₹{Math.round(bubble.bucket.low / 1000)}K–{Math.round(bubble.bucket.high / 1000)}K · {bubble.bucket.count} comp{bubble.bucket.count === 1 ? "" : "s"}
                </div>
              )}
              <ResponsiveContainer width="100%" height={isMob ? 244 : 292}>
                <BarChart data={data} margin={{ top: isMob ? 68 : 74, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={il ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)"} />
                  <XAxis dataKey="label" tick={{ fontSize: isMob ? 9.5 : 10.5, fontWeight: 600, fill: il ? "#4A4540" : "#cbd5e1", fontFamily: "'Manrope',sans-serif" }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis allowDecimals={false} domain={[0, (max) => Math.ceil((max || 1) * 1.45)]} tick={{ fontSize: 9, fill: il ? "#9A9490" : "#64748b", fontFamily: "'Manrope',sans-serif" }} axisLine={false} tickLine={false} width={22} />
                  {/* Same bucket → one merged pill (avoids a redundant stack that
                      would crowd the count label sitting right above that bar).
                      Different buckets → both pills sit at the same height since
                      they're already separated horizontally. */}
                  {medIdx >= 0 && medIdx === yourIdx ? (
                    <ReferenceLine x={hist.buckets[yourIdx].label} stroke={accent} strokeWidth={3}
                      label={<RefLinePill text="You · Median" bg={accent} fg={il ? "#fff" : "#080d16"} yOffset={2} phase={pillPhase} />} />
                  ) : (
                    <>
                      {medIdx >= 0 && (
                        <ReferenceLine x={hist.buckets[medIdx].label} stroke={il ? NEUTRAL_L : NEUTRAL_D} strokeOpacity={0.85} strokeWidth={2} strokeDasharray="5 3"
                          label={<RefLinePill text="Median" bg={il ? NEUTRAL_L : NEUTRAL_D} fg={il ? "#fff" : "#080d16"} yOffset={2} phase={pillPhase} />} />
                      )}
                      {yourIdx >= 0 && (
                        <ReferenceLine x={hist.buckets[yourIdx].label} stroke={accent} strokeWidth={3}
                          label={<RefLinePill text="You" bg={accent} fg={il ? "#fff" : "#080d16"} yOffset={2} phase={pillPhase} />} />
                      )}
                    </>
                  )}
                  <Bar dataKey="count" shape={barShape} isAnimationActive={false} onClick={(d) => onBarTap(d?.i)}>
                    <LabelList dataKey="count" content={countLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <div style={{ height: isMob ? 244 : 292 }} />}

          {tapped ? (
            <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.84rem", color: il ? "#0F0E0C" : "#e2e8f0", textAlign: "center", margin: "10px 0 0", lineHeight: 1.6 }}>
              ₹{Math.round(tapped.low / 1000)}K–{Math.round(tapped.high / 1000)}K/sqft — <strong style={{ color: il ? NEUTRAL_L : NEUTRAL_D }}>{tapped.count}</strong> comparable listing{tapped.count === 1 ? "" : "s"}
            </p>
          ) : pct != null ? (
            <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.84rem", color: il ? "#0F0E0C" : "#e2e8f0", textAlign: "center", margin: "10px 0 0", lineHeight: 1.6 }}>
              {/* "above" = pricier than the median (caution), "below" = cheaper (positive) — a real comparison, not "your number" identity, so it uses the semantic system, not accent. */}
              Your property sits <strong style={{ color: above ? (il ? CAUTION_L : CAUTION_D) : (il ? POSITIVE_L : POSITIVE_D) }}>{Math.abs(pct).toFixed(1)}% {above ? "above" : "below"}</strong> the {locality} median
            </p>
          ) : null}
        </GCard>
      </motion.div>

      {listingsData?.available && listingsData.listings.length >= 2 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.78rem", color: il ? NEUTRAL_L : NEUTRAL_D, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            Recent Listings
          </div>
          <GCard style={{ background: il ? "#F3FBFC" : "rgba(11,32,37,0.68)" }}>
            {listingsData.listings.slice(0, 4).map((l, i, arr) => (
              <ListingRow key={l.project_id} listing={l} yourPsf={ctx?.your_psf} il={il} isLast={i === arr.length - 1}
                cheaperColor={il ? POSITIVE_L : POSITIVE_D} pricierColor={il ? CAUTION_L : CAUTION_D} neutralColor={il ? NEUTRAL_L : NEUTRAL_D} />
            ))}
          </GCard>
        </div>
      )}

      {(() => {
        // Trend is its own real-volume check (a new endpoint — comps_used is
        // capped at 20, nowhere near the 15-20-per-window minimum a
        // defensible trend needs on both sides), independent of the driver
        // count below. When it clears that bar it just becomes one more
        // compact line here rather than a section of its own.
        const trendLine = trendData?.available && trendData.pct_change != null
          ? `Asking prices in ${locality} have ${trendData.pct_change >= 0 ? "risen" : "fallen"} ~${Math.abs(trendData.pct_change).toFixed(0)}% over the last 9 months`
          : null;
        const lines = [...driverLines, trendLine].filter(Boolean);
        if (!lines.length) return null;
        return (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.78rem", color: il ? NEUTRAL_L : NEUTRAL_D, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
              What's Driving the Price
            </div>
            <GCard style={{ background: il ? "#F3FBFC" : "rgba(11,32,37,0.68)" }}>
              {lines.map((line, i) => (
                <div key={i} style={{
                  display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0",
                  borderTop: i === 0 ? "none" : (il ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(255,255,255,0.06)"),
                }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: accent, marginTop: 7, flexShrink: 0 }} />
                  <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.78rem", color: il ? "#0F0E0C" : "#e2e8f0", lineHeight: 1.55, margin: 0 }}>{line}</p>
                </div>
              ))}
            </GCard>
          </div>
        );
      })()}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3 — THE CONTEXT: rental reality as plain text + a clean verdict
// badge with offer/walk-away text beneath — no gauges, no dials/needles.
// ─────────────────────────────────────────────────────────────────────────────
const VERDICT_ZONES = {
  GOOD_DEAL: { label: "Underpriced" },
  FAIRLY_PRICED: { label: "Fair" },
  OVERPRICED: { label: "Overpriced" },
};

function ContextSection({ result, rental, verdData, verdLoad }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const accent = il ? LC : C;
  const mid = result.estimated_range?.mid;
  if (mid == null) return null;
  const grossYield = rental?.available && mid ? ((rental.median_rent * 12 / mid) * 100).toFixed(1) : null;
  const recOffer = Math.round((mid * 0.96) / 100000) * 100000;
  const maxPay = Math.round((mid * 1.03) / 100000) * 100000;
  const zone = verdData ? VERDICT_ZONES[verdData.verdict] : null;
  // Semantic color resolved from the same cheaper/pricier/neutral system as
  // everywhere else — good deal reads as "cheaper", overpriced as "pricier",
  // fair as neutral. Never the accent — this is a judgement, not your number.
  const zoneColor = verdData?.verdict === "GOOD_DEAL" ? (il ? POSITIVE_L : POSITIVE_D)
    : verdData?.verdict === "OVERPRICED" ? (il ? CAUTION_L : CAUTION_D)
    : (il ? NEUTRAL_L : NEUTRAL_D);

  return (
    <section style={{ padding: isMob ? "8px 16px 20px" : "8px 40px 20px" }}>
      <SectionHeading>What This Means For You</SectionHeading>
      <GCard style={{ background: il ? "#FDFAF2" : "rgba(38,29,13,0.62)" }}>
        {rental?.available && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", paddingBottom: 16, marginBottom: 16, borderBottom: il ? "1px solid rgba(0,0,0,0.07)" : "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: il ? "rgba(0,189,204,0.08)" : "rgba(0,229,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>
            </div>
            <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.82rem", color: il ? "#0F0E0C" : "#e2e8f0", lineHeight: 1.6, margin: 0 }}>
              Rental potential: <strong>~{fmtCr(rental.median_rent)}/month</strong>{grossYield ? ` (~${grossYield}% yield)` : ""} based on {rental.count.toLocaleString()} listing{rental.count === 1 ? "" : "s"}
            </p>
          </div>
        )}

        {verdLoad ? (
          <div style={{ width: "40%", height: 24, borderRadius: 100, background: "rgba(255,255,255,0.06)", animation: "skPulse 2.2s ease-in-out infinite", marginBottom: 14 }} />
        ) : zone ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${zoneColor}18`, border: `1px solid ${zoneColor}44`, borderRadius: 100, padding: "6px 14px", marginBottom: 14 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: zoneColor }} />
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.78rem", color: zoneColor }}>{zone.label}</span>
          </div>
        ) : null}

        <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.85rem", color: il ? "#0F0E0C" : "#e2e8f0", lineHeight: 1.7, margin: 0 }}>
          Recommended offer: <strong style={{ color: accent }}>{fmtCr(recOffer)}</strong>.<br />
          Walk away above <strong>{fmtCr(maxPay)}</strong>.
        </p>
      </GCard>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 5 — BOOK INSPECTION (final CTA — boldest section on the page)
// ─────────────────────────────────────────────────────────────────────────────
function InspectionSection({ result, formData }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const score = result?.confidence?.score ?? 0;
  const accent = il ? LC : C;
  // The one deliberately loud moment on the page — full saturated accent
  // fill, not a tint, so the eye lands here after everything else has been
  // quiet/subtle. Text flips to whatever contrasts on that fill: white on
  // the darker light-theme LC, near-black on the bright dark-theme C.
  const onAccentText = il ? "#FFFFFF" : "#080d16";
  return (
    <section style={{ padding: isMob ? "8px 16px 48px" : "8px 40px 64px" }}>
      <GCard testId="intel-cta-block" style={{ textAlign: "center", background: accent, border: "none", boxShadow: il ? "0 8px 32px rgba(0,122,138,0.35)" : "0 8px 32px rgba(0,229,255,0.25)" }}>
        <div style={{ position: "absolute", top: -50, left: -50, width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${onAccentText}22 0%, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: isMob ? "1.15rem" : "1.35rem", color: onAccentText, margin: "0 0 8px" }}>Get certainty on this property</h3>
          <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.82rem", color: `${onAccentText}cc`, lineHeight: 1.6, margin: "0 0 20px" }}>
            A licensed inspector visits within 48 hours. Confidence jumps from {Math.round(score)} to 80.
          </p>
          <a href={waInspectionHref(result, formData)} target="_blank" rel="noopener noreferrer" data-testid="intel-cta-book"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "16px 22px",
              fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.95rem", borderRadius: 50, textDecoration: "none",
              background: onAccentText, color: accent, transition: "all 0.2s ease",
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
            Book inspection — ₹7,999
          </a>
          <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.7rem", color: `${onAccentText}99`, margin: "12px 0 0" }}>
            Direct WhatsApp — a human responds within 24 hours
          </p>
        </div>
      </GCard>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 4 — THE FULL STORY (collapsed accordion; ledger/rings/comp list)
// ─────────────────────────────────────────────────────────────────────────────
function TimelineStep({ n, title, detail, last }) {
  const { theme } = useTheme();
  const il = theme !== "dark";
  const accent = il ? LC : C;
  return (
    <div style={{ display: "flex", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{ width: 24, height: 24, borderRadius: "50%", background: accent, color: il ? "#fff" : "#080d16", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: "0.7rem" }}>{n}</div>
        {!last && <div style={{ width: 2, flex: 1, minHeight: 22, background: il ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)", margin: "4px 0" }} />}
      </div>
      <div style={{ paddingBottom: last ? 0 : 18 }}>
        <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.8rem", color: il ? "#0F0E0C" : "#e2e8f0" }}>{title}</div>
        <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.72rem", color: il ? "#6A6560" : "#94a3b8", marginTop: 3, lineHeight: 1.5 }}>{detail}</div>
      </div>
    </div>
  );
}

function CompsTable({ comps }) {
  const { theme } = useTheme();
  const il = theme !== "dark";
  const [showAll, setShowAll] = useState(false);
  const sorted = useMemo(() => [...comps].sort((a, b) => (b.weight || 0) - (a.weight || 0)), [comps]);
  const visible = showAll ? sorted : sorted.slice(0, 8);
  const cols = "1fr 0.55fr 0.8fr 0.85fr";
  const headStyle = { fontWeight: 700, color: il ? "#9A9490" : "#64748b", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.04em" };
  const cellStyle = { color: il ? "#4A4540" : "#e2e8f0" };
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, padding: "0 0 6px", borderBottom: il ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.08)", fontFamily: "'Manrope',sans-serif" }}>
        <div style={headStyle}>₹/sqft</div><div style={headStyle}>BHK</div><div style={headStyle}>Sqft</div><div style={headStyle}>Date</div>
      </div>
      {visible.map((c, i) => (
        <div key={c.source_msg_id || i} style={{ display: "grid", gridTemplateColumns: cols, gap: 8, padding: "7px 0", borderBottom: il ? "1px solid rgba(0,0,0,0.04)" : "1px solid rgba(255,255,255,0.04)", fontFamily: "'Manrope',sans-serif", fontSize: "0.74rem" }}>
          <div style={{ ...cellStyle, fontWeight: 700, color: il ? "#0F0E0C" : "#f1f5f9" }}>{fmtPsf(c.psf)}</div>
          <div style={cellStyle}>{c.bhk}</div>
          <div style={cellStyle}>{Math.round(c.sqft).toLocaleString()}</div>
          <div style={{ ...cellStyle, color: il ? "#9A9490" : "#94a3b8" }}>{fmtMonth(c.message_date)}</div>
        </div>
      ))}
      {sorted.length > 8 && (
        <button onClick={() => setShowAll((s) => !s)} style={{ marginTop: 12, background: "none", border: "none", cursor: "pointer", fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "0.72rem", color: il ? LC : C, padding: 0, transition: "all 0.2s ease" }}>
          {showAll ? "Show fewer" : `Show all ${sorted.length}`}
        </button>
      )}
    </>
  );
}

function FullStorySection({ result, formData }) {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const ledger = result.adjustment_ledger || [];
  const rings = result.ring_diagnostics || [];
  const comps = result.comps_used || [];
  const locality = result.locality_match?.matched_name || formData?.locality || "the locality";

  const step1 = ledger.find((l) => l.step === "weighted_ring_avg");
  const mult = ledger.find((l) => l.step === "sqft_multiplication");
  const adjSteps = ledger.filter((l) => l.step.endsWith("_adjustment") && l.step !== "asking_haircut");
  const appliedAdj = adjSteps.filter((l) => l.applied);
  const skippedAdj = adjSteps.filter((l) => !l.applied);
  // asking_haircut is always applied:false by design (engine never applies a
  // silent discount) — read from the ledger rather than hardcoded, so this
  // stays honest if that ever changes.
  const haircut = ledger.find((l) => l.step === "asking_haircut");
  const trustN = trustCompCount(result);
  const sqftLabel = Number(formData?.built_up_area || 0).toLocaleString();
  // The psf actually carried into the multiplication — this is step1.to only
  // when no adjustment changed it, so showing this (not step1.to blindly)
  // keeps Step 2 truthful even when floor/age adjustments did move the number.
  const finalPsf = mult?.from ?? step1?.to;
  const finalPrice = mult?.to ?? result.estimated_range?.mid;

  const sortedRings = [...rings].sort((a, b) => a.ring - b.ring);
  const firedRings = sortedRings.filter((r) => r.activated);
  const unfiredRings = sortedRings.filter((r) => !r.activated);
  const unfiredPlain = unfiredRings.map((r) => RING_PLAIN[r.ring] || r.label);
  const unfiredSentence = unfiredPlain.length === 1
    ? `We didn't have ${withArticle(unfiredPlain[0])} on file.`
    : unfiredPlain.length > 1
      ? `We didn't have ${unfiredPlain.map(lowerFirst).join(", ")} on file.`
      : "";

  const footnoteParts = [];
  if (skippedAdj.length) footnoteParts.push(`adjust for ${skippedAdj.map((a) => adjLabel(a.step.replace("_adjustment", "")).toLowerCase()).join(", ")}`);
  if (haircut && !haircut.applied) footnoteParts.push("apply any hidden discount");
  const footnoteSentence = footnoteParts.length
    ? `We didn't ${footnoteParts.join(", or ")} — we don't have that data yet, so we don't guess.`
    : "";

  return (
    <section style={{ padding: isMob ? "8px 16px 20px" : "8px 40px 20px" }}>
      <Collapsible title="The Full Story" badge="Full method, math, and comp list" defaultOpen={false} muted
        icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={il ? NEUTRAL_L : NEUTRAL_D} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /></svg>}>

        {(step1 || mult) && (
          <SubAccordion title="The Math, Simplified">
            {step1 && (
              <TimelineStep n={1} title="Found comparable properties"
                detail={`We found ${trustN.toLocaleString()} propert${trustN === 1 ? "y" : "ies"} like yours in ${locality} and averaged their price: ${fmtPsf(step1.to)}/sqft`} />
            )}
            {mult && (
              <TimelineStep n={2} title="Priced by size"
                detail={`${fmtPsf(finalPsf)}/sqft × ${sqftLabel} sqft${appliedAdj.length ? ` (adjusted for ${appliedAdj.map((a) => adjLabel(a.step.replace("_adjustment", "")).toLowerCase()).join(", ")})` : ""}`} />
            )}
            <TimelineStep n={3} title="Your estimate" detail={fmtCr(finalPrice)} last />
            {footnoteSentence && (
              <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.66rem", color: il ? "#9A9490" : "#64748b", marginTop: 10, lineHeight: 1.5 }}>
                {footnoteSentence}
              </p>
            )}
          </SubAccordion>
        )}

        {rings.length > 0 && (
          <SubAccordion title="How We Found Your Matches">
            {firedRings.map((r) => (
              <div key={r.ring} style={{ padding: "6px 0", fontFamily: "'Manrope',sans-serif", fontSize: "0.78rem", color: il ? "#0F0E0C" : "#e2e8f0" }}>
                ✓ {RING_PLAIN[r.ring] || r.label} → <strong>{r.comps_found}</strong> found
              </div>
            ))}
            {unfiredSentence && (
              <div style={{ padding: "6px 0", fontFamily: "'Manrope',sans-serif", fontSize: "0.7rem", color: il ? "#9A9490" : "#64748b", lineHeight: 1.6 }}>
                {unfiredSentence}
              </div>
            )}
            <div style={{ padding: "6px 0", fontFamily: "'Manrope',sans-serif", fontSize: "0.7rem", color: il ? "#9A9490" : "#64748b", lineHeight: 1.6 }}>
              Unlike inflated confidence scores elsewhere, ours reflects real data limits — asking prices only, until verified by inspection.
            </div>
          </SubAccordion>
        )}

        {comps.length > 0 && (
          <SubAccordion title="All comparable listings" badge={`${comps.length} listing${comps.length === 1 ? "" : "s"}`}>
            <CompsTable comps={comps} />
          </SubAccordion>
        )}
      </Collapsible>

      {(result.message || result.igr_note) && (
        <div style={{ marginTop: 14 }}>
          <GCard style={{ background: il ? "#F6F5F3" : "rgba(22,22,24,0.6)", border: il ? "1.5px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.06)", boxShadow: "none" }}>
            {result.message && <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.78rem", color: il ? "#0F0E0C" : "#e2e8f0", lineHeight: 1.7, margin: result.igr_note ? "0 0 8px" : 0 }}>{result.message}</p>}
            {result.igr_note && <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.7rem", color: il ? "#9A9490" : "#64748b", lineHeight: 1.6, margin: 0 }}>🏛️ {result.igr_note}</p>}
          </GCard>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOADING / ERROR states
// ─────────────────────────────────────────────────────────────────────────────
function LoadingSkeleton() {
  const isMob = useIsMobile();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const bar = (w, h = 12, extra = {}) => (
    <div style={{ width: w, height: h, borderRadius: 6, background: il ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)", animation: "skPulse 2.2s ease-in-out infinite", ...extra }} />
  );
  return (
    <section style={{ minHeight: "calc(100vh - 58px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isMob ? "40px 20px" : "60px 40px" }}>
      <div style={{ marginBottom: 18 }}>{bar(120, 10)}</div>
      <div style={{ marginBottom: 20 }}>{bar(isMob ? 260 : 420, isMob ? 44 : 64)}</div>
      <div style={{ marginBottom: 22 }}>{bar(200, 14)}</div>
      <div style={{ marginBottom: 26 }}>{bar(160, 12)}</div>
      {bar(150, 28, { borderRadius: 100 })}
    </section>
  );
}

function ErrorState({ message, onRetry }) {
  const { theme } = useTheme();
  const il = theme !== "dark";
  return (
    <section style={{ padding: "60px 20px" }}>
      <GCard testId="intel-error-state" style={{ textAlign: "center", padding: "40px 28px" }}>
        <div style={{ fontSize: 34, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "1.1rem", color: il ? "#0F0E0C" : "#f1f5f9", margin: "0 0 8px" }}>Couldn't load your valuation</h2>
        <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.8rem", color: il ? "#6A6560" : "#94a3b8", lineHeight: 1.6, margin: "0 0 20px" }}>{message}</p>
        <CTABtn variant="cyan" onClick={onRetry} testId="intel-error-retry">Try again</CTABtn>
      </GCard>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
const IntelligenceResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const il = theme !== "dark";
  const isMobile = useIsMobile();

  const initial = useMemo(() => readHandoff(location), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [result, setResult] = useState(initial.result);
  const [formData] = useState(initial.formData);
  const [status, setStatus] = useState(
    initial.result ? "ready" : initial.formData ? "loading" : "redirect"
  );
  const [errorMsg, setErrorMsg] = useState("");

  const [verdData, setVerdData] = useState(null);
  const [verdLoad, setVerdLoad] = useState(true);
  const didFireVerdict = useRef(false);

  const goNew = useCallback(() => navigate("/valuate"), [navigate]);

  const fetchValuation = useCallback(async () => {
    if (!formData) { setStatus("redirect"); return; }
    setStatus("loading");
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${API}/valuate`, buildValuatePayload(formData));
      try { sessionStorage.setItem("pq_result", JSON.stringify(data)); } catch { /* ignore */ }
      setResult(data);
      setStatus("ready");
    } catch (e) {
      setErrorMsg("The valuation engine didn't respond. Please try again.");
      setStatus("error");
    }
  }, [formData]);

  useEffect(() => {
    if (status === "redirect") { navigate("/valuate"); return; }
    if (status === "loading" && !initial.result) fetchValuation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, []);

  const hasEstimate = !!(result && result.estimated_range && result.estimated_range.mid != null);

  // Deal verdict — feeds Section 5's compass indicator. Fires once.
  useEffect(() => {
    if (status !== "ready" || !hasEstimate || !formData || didFireVerdict.current) return;
    didFireVerdict.current = true;
    const asking = formData.asking_price ? parseFloat(formData.asking_price) : null;
    axios.post(`${API}/deal-verdict`, {
      locality: formData.locality ?? null,
      estimated_price_per_sqft: result.estimated_psf?.mid ?? result.psf_context?.this_unit ?? result.psf_context?.locality_median ?? null,
      estimated_value: result.estimated_range?.mid ?? null,
      asking_price: asking || null,
    })
      .then((r) => setVerdData(r.data))
      .catch(() => setVerdData(null))
      .finally(() => setVerdLoad(false));
  }, [status, hasEstimate, formData, result]);

  const localityId = result?.locality_match?.id;
  const { data: rental } = useRentalComps(localityId, status === "ready" && hasEstimate);
  const listingsData = useRecentListings(localityId, formData?.bhk ? parseInt(formData.bhk) : null, status === "ready" && hasEstimate);
  const trendData = usePriceTrend(localityId, status === "ready" && hasEstimate);

  if (status === "redirect") return null;

  const styleBlock = (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap');
      input,textarea{font-size:16px!important;}
      @keyframes skPulse{0%,100%{opacity:0.4}50%{opacity:0.75}}
      @keyframes pulse-ring{0%{transform:scale(1);opacity:.7}100%{transform:scale(2.4);opacity:0}}
      button, a { transition: all 0.2s ease; }
    `}</style>
  );

  const Nav = (
    <div style={{ position: "sticky", top: 0, zIndex: 200, height: 58, display: "flex", alignItems: "center", borderBottom: il ? "1px solid rgba(0,0,0,0.07)" : "1px solid rgba(255,255,255,0.05)", background: il ? "rgba(238,246,248,0.94)" : "rgba(8,13,22,0.93)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: isMobile ? "0 14px" : "0 40px", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={goNew} data-testid="intel-back-to-form" style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: "'Manrope',sans-serif", fontSize: "0.79rem", color: il ? "#6A6560" : "rgba(255,255,255,0.6)", background: "none", border: "none", cursor: "pointer" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>New Valuation
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}><img src="/logo.png" alt="Propspector logo" style={{ width: 26, height: 26, objectFit: "contain", display: "block" }} /></div>
          <span style={{ fontWeight: 700, fontSize: "0.95rem", fontFamily: "'Sora',sans-serif", color: il ? "#0F0E0C" : "#fff" }}>Propspector</span>
        </div>
        <button onClick={() => navigate("/")} data-testid="intel-home" style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: "'Manrope',sans-serif", fontSize: "0.79rem", color: il ? "#6A6560" : "rgba(255,255,255,0.6)", background: "none", border: "none", cursor: "pointer" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>Home
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: il ? LIGHT_BG : BG, fontFamily: "'Sora',sans-serif", color: il ? "#0F0E0C" : "#fff", overflowX: "hidden" }}>
      {styleBlock}
      {Nav}
      <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto" }}>
        {status === "loading" && <LoadingSkeleton />}
        {status === "error" && <ErrorState message={errorMsg} onRetry={fetchValuation} />}

        {status === "ready" && !hasEstimate && (
          <>
            <HeroEmpty result={result} formData={formData} />
            <InspectionSection result={result} formData={formData} />
          </>
        )}

        {status === "ready" && hasEstimate && (
          <>
            <HeroVerdict result={result} formData={formData} rental={rental} />
            <PropertySummaryRow result={result} formData={formData} />
            <DistributionSection result={result} formData={formData} listingsData={listingsData} trendData={trendData} />
            <ContextSection result={result} rental={rental} verdData={verdData} verdLoad={verdLoad} />
            <FullStorySection result={result} formData={formData} />
            <InspectionSection result={result} formData={formData} />
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", padding: "0 16px 56px" }}>
              <CTABtn variant="ghost" onClick={goNew} testId="intel-cta-new">New Valuation</CTABtn>
              <CTABtn variant="ghost" onClick={() => navigate("/")} testId="intel-cta-home">Back to Home</CTABtn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IntelligenceResults;
